/**
 * R5 and R6's sheet: four reason rows, an optional capped details field, a
 * "Block them too" checkbox off by default, a "Send report" primary, and the
 * persistent inline sent state. The checkbox is not rendered on the viewer's
 * own message, because blocking yourself is not a thing (ISSUE-1049).
 *
 * Rendered through `Dialog`, a bottom sheet under 768px and a centered
 * dialog at 768px and above, with no viewport branching in this file.
 *
 * A sent report REPLACES the form rather than disabling it: `design-prompts/
 * 51` calls for "a persistent inline state on the sheet", not a transient
 * banner, so the reason list, details field and checkbox are gone entirely
 * once the report has been sent. A failed "Block them too" leg does not stop
 * the sent state from showing: the report really was sent, and R6 requires
 * the confirmation for it regardless of whether the block leg succeeded. The
 * block failure has already raised the shared error modal from
 * `useBlockFan.ts`'s orchestration, so the fan is told rather than left with
 * a silent half-applied moderation action.
 */
import { Button, Checkbox, Dialog, Input, Radio } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import {
  BLOCK_THEM_TOO_LABEL,
  normalizeReportDetails,
  REPORT_DETAILS_LABEL,
  REPORT_DETAILS_MAX_LENGTH,
  REPORT_DONE_LABEL,
  REPORT_REASON_LABELS,
  REPORT_REASONS,
  REPORT_REASONS_GROUP_LABEL,
  REPORT_SENT_LINE,
  REPORT_SHEET_TITLE,
  SEND_REPORT_LABEL,
  type typeReportReason,
} from "./moderationCopy";
import { isViewerSender } from "./moderationRules";
import { useReportMessage } from "./useReportMessage";

export type typeReportMessageDialogProps = {
  /** Driven by ?dialog=report-message, never by component state. */
  open: boolean;
  onClose: () => void;
  messageId: string;
  senderAccountId: string;
};

const REASON_ITEMS = REPORT_REASONS.map((reason) => ({
  value: reason,
  label: REPORT_REASON_LABELS[reason],
}));

export const ReportMessageDialog = (
  props: typeReportMessageDialogProps,
): ReactNode => {
  const { sendReport, isPending } = useReportMessage();
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : "";
  const isOwnMessage = isViewerSender({
    senderId: props.senderAccountId,
    viewerUserId,
  });
  const [reason, setReason] = useState<typeReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [blockThemToo, setBlockThemToo] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const handleSend = async (): Promise<void> => {
    if (reason === null) return;
    const outcome = await sendReport({
      messageId: props.messageId,
      reason,
      details: normalizeReportDetails(details),
      blockThemToo,
      senderAccountId: props.senderAccountId,
    });
    if (outcome.didSend) setIsSent(true);
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={REPORT_SHEET_TITLE}
      footer={
        isSent ? undefined : (
          <Button
            variant="primary"
            fullWidth
            data-testid="report-send"
            disabled={reason === null || isPending}
            loading={isPending}
            onClick={() => {
              void handleSend();
            }}
          >
            {SEND_REPORT_LABEL}
          </Button>
        )
      }
    >
      {isSent ? (
        <>
          <p data-testid="report-sent-line">{REPORT_SENT_LINE}</p>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              props.onClose();
            }}
          >
            {REPORT_DONE_LABEL}
          </Button>
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <Radio
            label={REPORT_REASONS_GROUP_LABEL}
            items={REASON_ITEMS}
            value={reason ?? undefined}
            onValueChange={(next) => {
              setReason(next as typeReportReason);
            }}
          />
          <Input
            label={REPORT_DETAILS_LABEL}
            value={details}
            maxLength={REPORT_DETAILS_MAX_LENGTH}
            onChange={(event) => {
              setDetails(event.target.value);
            }}
            data-testid="report-details"
          />
          {isOwnMessage ? null : (
            <Checkbox
              label={BLOCK_THEM_TOO_LABEL}
              checked={blockThemToo}
              onCheckedChange={(next) => {
                setBlockThemToo(next === true);
              }}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};
